import React, { useEffect, useState } from "react";
import axios from "axios";
import { useForm } from "react-hook-form";
import { useNavigate, useParams, Link } from "react-router-dom";
import { MdOutlineLock, MdArrowForward, MdCheckCircle, MdError } from "react-icons/md";
import { RiShieldKeyholeLine } from "react-icons/ri";
import logo from "../../assets/img/logo.png";

const AuthLogin = () => {
  /* get REACT_APP_API_URL from .env file */
  const { REACT_APP_BASE_URL } = process.env;
  const { REACT_APP_API_URL } = process.env;
  const { REACT_APP_CookieURL } = process.env;
  const { REACT_APP_ANALYTICS_BASE_URL, REACT_APP_WEBSITE_URL } = process.env;

  const { register, handleSubmit, formState: { errors } } = useForm({ mode: "onTouched" });

  const { companyName } = useParams() || "";
  /* get values from URL parameters */
  const { surveyId } = useParams() || "";
  const [loading, setLoading] = useState(false);
  const [pinValue, setPinValue] = useState("");
  /* variable declarations */
  let history = useNavigate();

  const [toggleSuccess, setToggleSuccess] = useState("");
  const [toggleError, setToggleError] = useState("");

  const onToggleSuccess = (message) => {
    setToggleSuccess(message);
    if (message !== "") {
      setTimeout(() => {
        setToggleSuccess("");
      }, 4000);
    }
  };

  const onToggleError = (message) => {
    setToggleError(message);
    if (message !== "") {
      setTimeout(() => {
        setToggleError("");
      }, 4000);
    }
  };

  /* slide method declare in parent */
  const next = () => {
    setLoading(true);
    if (pinValue === "261092") {
      fastLogin();
    } else {
      onToggleError("The PIN you have entered is invalid");
      setLoading(false);
    }
  };

  const getUserData = async (token) => {
    /* get user data */
    await axios
      .get(`${REACT_APP_API_URL}/user`, {
        headers: {
          Authorization: "Bearer " + token,
        },
      })
      .then((result) => {
        if (result.data.data) {
          localStorage.setItem("user", window.btoa(JSON.stringify(result.data.data)));
        } else {
          onToggleError("No data found.");
        }
      })
      .catch((err) => {
        onToggleError("Something went wrong.");
      });
  };

  const fastLogin = () => {
    axios
      .post(`${REACT_APP_API_URL}/AnalyticsLogin`, { survey_id: surveyId })
      .then(async (result) => {
        if (result.status) {
          await getUserData(result.data.access_token, true);
          sessionStorage.setItem("analytics_token", result.data.access_token);
          window.location = `${REACT_APP_ANALYTICS_BASE_URL}/${companyName}/Analytics/${surveyId}`;
        }
      })
      .catch((err) => {
        if (err.response) {
          onToggleError("Invalid credentials. Please try again.");
        } else {
          onToggleError("Something went wrong. Please try again.");
        }
      });
  };

  useEffect(() => { }, [history, REACT_APP_API_URL, REACT_APP_BASE_URL, surveyId, companyName]);

  // Compute filled dots based on pin length
  const filledCount = pinValue ? pinValue.length : 0;

  return (
    <div className="login-page-wrapper">
      {/* Header */}
      <header className="login-header">
        <div className="login-header-logo">
          <Link to={{ pathname: `${REACT_APP_WEBSITE_URL}` }} target="_blank">
            <img src={logo} alt="CENSUS by OpenEyes" />
          </Link>
        </div>
        <div className="login-header-badge">
          Survey Analytics
        </div>
      </header>

      {/* Content */}
      <div className="login-content-area">
        <div className="login-card">
          {/* Icon */}
          <div className="login-icon-wrap">
            <RiShieldKeyholeLine />
          </div>

          {/* Titles */}
          <h1 className="login-card-title">Secure Access</h1>
          <p className="login-card-subtitle">
            Enter your 6-digit PIN to access the survey analytics dashboard
          </p>

          {/* Form */}
          <form onSubmit={handleSubmit(next)}>
            {loading ? (
              <div className="login-loading-state">
                <div className="loading-spinner-wrap" />
                <p>Authenticating, please wait…</p>
              </div>
            ) : (
              <>
                <label className="login-form-label" htmlFor="pinInput">
                  <span className="required-star">*</span>
                  6-digit PIN number
                </label>

                <div className="login-input-wrap">
                  <input
                    name="pinNumber"
                    autoComplete="off"
                    id="pinInput"
                    type="text"
                    className="pin-input"
                    placeholder="• • • • • •"
                    maxLength="6"
                    {...register('pinNumber', {
                      required: true,
                      pattern: /^[0-9]+$/i,
                      minLength: 6,
                      maxLength: 6,
                    })}
                    onChange={(e) => {
                      setPinValue(e.target.value);
                    }}
                  />
                  {/* PIN progress dots */}
                  <div className="pin-dots">
                    {[...Array(6)].map((_, i) => (
                      <span key={i} className={i < filledCount ? "filled" : ""} />
                    ))}
                  </div>
                </div>

                {errors.pinNumber && errors.pinNumber.type === "required" && (
                  <p className="login-error-text">PIN number is required.</p>
                )}
                {errors.pinNumber && errors.pinNumber.type === "pattern" && (
                  <p className="login-error-text">Please enter a valid numeric PIN.</p>
                )}
                {errors.pinNumber && errors.pinNumber.type === "minLength" && (
                  <p className="login-error-text">PIN must be exactly 6 digits.</p>
                )}
                {errors.pinNumber && errors.pinNumber.type === "maxLength" && (
                  <p className="login-error-text">PIN must be exactly 6 digits.</p>
                )}

                <button className="login-submit-btn" type="submit">
                  Access Analytics
                  <MdArrowForward />
                </button>
              </>
            )}
          </form>
        </div>
      </div>

      {/* Footer */}
      <footer className="login-footer-area">
        <div className="login-footer-inner">
          <div className="footer-brand">
            Powered by&nbsp;
            <a href="https://theopeneyes.com" target="_blank" rel="noreferrer">
              OpenEyes Technologies Inc.
            </a>
          </div>
          <div className="footer-links">
            <a href="https://theopeneyes.com/privacy-policy" target="_blank" rel="noreferrer">
              Privacy Policy
            </a>
            <a href="https://theopeneyes.com/terms-of-use" target="_blank" rel="noreferrer">
              Terms of Use
            </a>
            <a href="https://theopeneyes.com/cookie-policy" target="_blank" rel="noreferrer">
              Cookie Policy
            </a>
          </div>
        </div>
      </footer>

      {/* Toast Notifications */}
      {toggleSuccess !== "" && (
        <div className="login-toast login-toast-success">
          <MdCheckCircle style={{ fontSize: 20, flexShrink: 0 }} />
          {toggleSuccess}
          <button className="toast-close" onClick={() => setToggleSuccess("")}>&times;</button>
        </div>
      )}

      {toggleError !== "" && (
        <div className="login-toast login-toast-error">
          <MdError style={{ fontSize: 20, flexShrink: 0 }} />
          {toggleError}
          <button className="toast-close" onClick={() => setToggleError("")}>&times;</button>
        </div>
      )}
    </div>
  );
};

export default AuthLogin;
